import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import {
  Card,
  Typography,
  Box,
  Button,
  InputLabel,
  Skeleton,
  TextField,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import useStyles from "./EVESensitivityModelerGlobal.styles";
import { useUserContext } from "../../../context/UserContext";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext";
import {
  getMappedBusinessEntityIDs,
  prepareDataForMarketValue,
} from "../../../utils/utils";
import {
  GLOBAL_DV100,
  GLOBALMARKETVALUE,
  MEASURE,
  DIMENSION,
} from "../../../utils/constants";
import ProgressSteps from "../../common/Steps/ProgressSteps";
import { useApprovalContext } from "../../../context/ApprovalContext";
import EVESensitivityModelerGlobalExport from "./EVESensitivityModelerGlobalExport";

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const emptyDatas = () => ({
  Balance: 0,
  MarketValue: 0,
  MarketValueUP100: 0,
  DV100Sensitivity: 0,
});

const addDatas = (a, b) => ({
  Balance: Number(a.Balance) + Number(b.Balance),
  MarketValue: Number(a.MarketValue) + Number(b.MarketValue),
  MarketValueUP100: Number(a.MarketValueUP100) + Number(b.MarketValueUP100),
  DV100Sensitivity: Number(a.DV100Sensitivity) + Number(b.DV100Sensitivity),
});

const findAccountByName = (accounts, name) => {
  for (const acc of accounts || []) {
    if (acc.reportingAccountName === name) {
      return acc.datas || emptyDatas();
    }
    if (acc.subAccounts?.length) {
      const found = findAccountByName(acc.subAccounts, name);
      if (found) return found;
    }
  }
  return null;
};

const EVESensitivityModelerGlobalFiltersAndReports = ({
  toSave = false,
  toCompare = false,
} = {}) => {
  const { classes } = useStyles(toCompare);

  const [isWritableSfc, setIsWritableSfc] = useState(false);
  const { selectedUserDetailLoading, selectedUserDetail, isAdmin } =
    useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    localCurrency,
    setLocalCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    selectedMonthEnd,
    setSelectedMonthEnd,
    marketValueReport,
    setMarketValueReport,
    marketValueReportLoading,
    marketValueReportError,
    fetchMarketValueReport,
  } = useHedgeRatioContext();

  const { metricsHistoryUpsertAndSave, metricsHistoryUpsertAndSaveLoading } =
    useTreasuryKRISummaryContext();

  const {
    fetchApprovalFlowData,
    approvalFlow,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const { regions, email } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency, writable } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
    setIsWritableSfc(writable);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  useEffect(() => {
    if (selectedModelDatabase) {
      const modelObject = modelDatabases?.find((model) => {
        return model?.modelName === selectedModelDatabase;
      });
      const monthEndFromModelDatabase = modelObject?.monthEnd
        ? dayjs(modelObject.monthEnd?.replace("Z", ""))
        : null;
      setSelectedMonthEnd(monthEndFromModelDatabase);
    }
  }, [modelDatabases, selectedModelDatabase, setSelectedMonthEnd]);

  const APPROVAL_FLOW_PARAMS = {
    region: null,
    businessEntityID: selectedBusinessEntityID,
    selectedReportDate: selectedMonthEnd,
    isGlobal: true,
    isModelerReport: true,
    isTreasuryKRIReport: false,
    isKRISummaryReport: false,
    isSFCKRIGlobalSummaryReport: false,
    isRegionKRIGlobalSummaryReport: false,
    isTFSCKRIGlobalSummaryReport: false,
    measure: MEASURE.DV100_INTEREST_RATE_SENSITIVITY_EQUITY_PERCENTAGE,
    dimension: DIMENSION.INTEREST_RATE_RISK,
    dimension2: null,
  };

  const handleSubmitBtn = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      fetchMarketValueReport(GLOBAL_DV100);
      fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
    } else {
      setMarketValueReport([]);
      setApprovalFlow([]);
    }
    fetchMarketValueReport();
  };

  const handleSendApproverEmailNotify = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => {
        return fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
      })
      .then(() => {
        return sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        });
      });
  };

  const scaleAndFormatData = useCallback((accounts, divisor, decimals) => {
    if (!accounts || accounts.length === 0) {
      return [];
    }

    return accounts.map((account) => {
      const newDatas = {};
      for (const [key, value] of Object.entries(account.datas || {})) {
        const scaledValue = Number(value) / divisor;
        const formattedValue = scaledValue.toFixed(decimals);
        newDatas[key] = formattedValue;
      }

      let newSubAccounts = [];
      if (account.subAccounts && account.subAccounts.length > 0) {
        newSubAccounts = scaleAndFormatData(
          account.subAccounts,
          divisor,
          decimals,
        );
      }
      return {
        ...account,
        datas: newDatas,
        subAccounts: newSubAccounts,
      };
    });
  }, []);

  const columns = [
    {
      id: "reportingAccountName",
      label: "Accounts",
      minWidth: 270,
      align: "left",
    },
    {
      id: "Balance",
      label: "Balance",
      minWidth: 270,
      align: "left",
    },
    {
      id: "MarketValue",
      label: "Market Value",
      minWidth: 270,
      align: "left",
    },
    {
      id: "MarketValueUP100",
      label: "Market Value:UP 100",
      minWidth: 270,
      align: "left",
    },
    {
      id: "DV100Sensitivity",
      label: "DV100 Sensitivity",
      minWidth: 270,
      align: "left",
    },
    {
      id: "PercentageDV100SensitivityOverBalance",
      label: "% DV100 Sensitivity over Balance",
      minWidth: 220,
      align: "left",
    },
    {
      id: "PercentageDV100OverMktValue",
      label: "%DV100 over Mkt Value",
      minWidth: 220,
      align: "left",
    },
    {
      id: "PercentageDV100ContributionToEquity",
      label: "% DV100 Contribution to Equity",
      minWidth: 220,
      align: "left",
    },
  ];

  const handleSaveDV100PercentInEquity = () => {
    const datasTotal = datasForTotal(false);
    const netEquityObject = datasTotal?.find(
      (obj) => obj.reportingAccountName === GLOBALMARKETVALUE.GLOBAL_NET_EQUITY,
    );

    metricsHistoryUpsertAndSave([
      {
        dimension: DIMENSION.INTEREST_RATE_RISK,
        dimension2: null,
        measure: MEASURE.DV100_INTEREST_RATE_SENSITIVITY_EQUITY_PERCENTAGE,
        businessEntityID: selectedBusinessEntityID,
        value: `${netEquityObject?.PercentageDV100ContributionToEquity}`,
        modelDatabase: selectedModelDatabase,
        isGlobal: true,
      },
    ]);
  };

  const decimals =
    Number(selectedDecimalsIn) >= 0 ? Number(selectedDecimalsIn) : 0;
  const divisor = Number(selectedFigureDivisor) || 1;

  const datasForTotal = useMemo(
    () => (isScaleAndFormattable) => {
      if (!marketValueReport?.length) return [];

      const {
        GLOBAL_TOTAL_ASSETS,
        GLOBAL_TOTAL_LIABILITIES,
        GLOBAL_TOTAL_EQUITY,
        GLOBAL_OFF_BALANCE_SHEET,
        GLOBAL_AUXILLIARY,
        GLOBAL_NET_EQUITY,
      } = GLOBALMARKETVALUE || {};

      const totalAssets =
        findAccountByName(marketValueReport, GLOBAL_TOTAL_ASSETS) ||
        emptyDatas();

      const totalLiabilities =
        findAccountByName(marketValueReport, GLOBAL_TOTAL_LIABILITIES) ||
        emptyDatas();

      const totalEquityRaw = addDatas(totalAssets, totalLiabilities);

      const totalEquity = {
        ...totalEquityRaw,
        Balance: totalEquityRaw?.Balance * -1,
      };

      const offBalanceSheet =
        findAccountByName(marketValueReport, GLOBAL_OFF_BALANCE_SHEET) ||
        emptyDatas();

      const auxilliary =
        findAccountByName(marketValueReport, GLOBAL_AUXILLIARY) || emptyDatas();

      const netEquity = [totalEquity, offBalanceSheet, auxilliary].reduce(
        (acc, curr) => addDatas(acc, curr),
        emptyDatas(),
      );

      const percent = (numerator, denominator) =>
        denominator !== 0
          ? ((numerator / denominator) * 100).toFixed(2) + "%"
          : "-";

      const buildRow = (name, data, bold = false) => ({
        reportingAccountName: name,
        ...data,
        PercentageDV100SensitivityOverBalance: percent(
          Number(data.DV100Sensitivity),
          Math.abs(Number(data.Balance)),
        ),
        PercentageDV100OverMktValue: percent(
          Number(data.DV100Sensitivity),
          Math.abs(Number(data.MarketValue)),
        ),
        PercentageDV100ContributionToEquity: percent(
          Number(data.DV100Sensitivity),
          Math.abs(Number(netEquity.Balance)),
        ),
        ...(bold && {
          cellStyles: {
            fontSize: "13px",
            fontWeight: "bold",
            backgroundColor: "#F5F5F5",
            borderTop: "1px solid #000000",
            borderBottom: "1px solid #000000",
          },
        }),
      });
      return [
        buildRow(
          GLOBAL_TOTAL_ASSETS,
          isScaleAndFormattable
            ? scaleAndFormatData([{ datas: totalAssets }], divisor, decimals)[0]
                .datas
            : totalAssets,
        ),
        buildRow(
          GLOBAL_TOTAL_LIABILITIES,
          isScaleAndFormattable
            ? scaleAndFormatData(
                [{ datas: totalLiabilities }],
                divisor,
                decimals,
              )[0].datas
            : totalLiabilities,
        ),
        buildRow(
          GLOBAL_TOTAL_EQUITY,
          isScaleAndFormattable
            ? scaleAndFormatData([{ datas: totalEquity }], divisor, decimals)[0]
                .datas
            : totalEquity,
        ),
        buildRow(
          GLOBAL_OFF_BALANCE_SHEET,
          isScaleAndFormattable
            ? scaleAndFormatData(
                [{ datas: offBalanceSheet }],
                divisor,
                decimals,
              )[0].datas
            : offBalanceSheet,
        ),
        buildRow(
          GLOBAL_AUXILLIARY,
          isScaleAndFormattable
            ? scaleAndFormatData([{ datas: auxilliary }], divisor, decimals)[0]
                .datas
            : auxilliary,
        ),
        buildRow(
          GLOBAL_NET_EQUITY,
          isScaleAndFormattable
            ? scaleAndFormatData([{ datas: netEquity }], divisor, decimals)[0]
                .datas
            : netEquity,
          true,
        ),
      ];
    },
    [decimals, divisor, marketValueReport, scaleAndFormatData],
  );

  const preprocessedData = useMemo(
    () => (isScaleAndFormattable) => {
      if (!marketValueReport?.length) return [];
      const {
        GLOBAL_TOTAL_ASSETS,
        GLOBAL_TOTAL_LIABILITIES,
        GLOBAL_TOTAL_EQUITY,
        GLOBAL_OFF_BALANCE_SHEET,
        GLOBAL_AUXILLIARY,
        GLOBAL_NET_EQUITY,
      } = GLOBALMARKETVALUE || {};

      const orderBy = [
        GLOBAL_TOTAL_ASSETS,
        GLOBAL_TOTAL_LIABILITIES,
        GLOBAL_TOTAL_EQUITY,
        GLOBAL_OFF_BALANCE_SHEET,
        GLOBAL_AUXILLIARY,
      ];

      const orderMap = orderBy.reduce((obj, item, index) => {
        return {
          ...obj,
          [item]: index,
        };
      }, {});

      marketValueReport.sort((a, b) => {
        const priorityA = orderMap[a.reportingAccountName];
        const priorityB = orderMap[b.reportingAccountName];
        return priorityA - priorityB;
      });

      const netEquityRow = datasForTotal(isScaleAndFormattable).find(
        (row) => row.reportingAccountName === GLOBAL_NET_EQUITY,
      );

      const netEquityBalance = netEquityRow?.Balance || 0;

      return prepareDataForMarketValue(
        isScaleAndFormattable
          ? scaleAndFormatData(marketValueReport, divisor, decimals)
          : marketValueReport,
        netEquityBalance,
      );
    },
    [marketValueReport, datasForTotal, scaleAndFormatData, divisor, decimals],
  );

  return (
    <Card
      className={classes.EVESensitivityModelerGlobalCardResponsive}
      style={{
        padding: "6px",
        marginBottom: "20px",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the EVE Sensitivity (Modeler Global View) Report
        based on the selected filters.
      </Typography>
      <>
        {!marketValueReportError &&
        marketValueReport &&
        marketValueReport?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pr: "10px",
              }}
            >
              {approvalFlow?.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSendApproverEmailNotify}
                  sx={{
                    fontSize: "0.8rem",
                    boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                    height: "38px",
                    mt: "-10px",
                  }}
                  disabled={
                    selectedUserDetailLoading ||
                    hrFiltersDataLoading ||
                    marketValueReportLoading ||
                    !selectedBusinessEntityID ||
                    !selectedModelDatabase ||
                    sendEmailNotifyLoading
                  }
                >
                  Send Reminder Notification
                </Button>
              )}
            </Box>

            <ProgressSteps
              stepsData={approvalFlow}
              isGlobal={true}
              isModelerReport={true}
              isTreasuryKRIReport={false}
              isKRISummaryReport={false}
              isSFCKRIGlobalSummaryReport={false}
              isRegionKRIGlobalSummaryReport={false}
              isTFSCKRIGlobalSummaryReport={false}
              reportDate={selectedMonthEnd}
              loggedInEmail={email}
              handleStepApproval={handleStepApproval}
            />
          </>
        ) : (
          <Typography
            sx={{
              color: "#6a6a6a",
              marginBottom: 2,
              fontStyle: "italic",
            }}
          >
            Please generate the report to view the Approval Progress
          </Typography>
        )}
      </>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"model-database"}>Model Database</InputLabel>
          <Autocomplete
            options={mappedModelDatabases}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedModelDatabases.find(
                (item) => item.id === selectedModelDatabase,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedModelDatabase(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Model Database"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitBtn}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              marketValueReportLoading ||
              !selectedBusinessEntityID ||
              !selectedModelDatabase
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              marketValueReportLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              marketValueReportLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.EVESensitivityModelerGlobalExportBtn}>
            <EVESensitivityModelerGlobalExport
              columns={columns}
              datasForTotal={datasForTotal(false)}
              datasForReport={preprocessedData(false)}
              disabled={
                marketValueReportLoading ||
                !marketValueReport?.length ||
                marketValueReportError
              }
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!marketValueReportLoading && marketValueReportError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : !marketValueReportLoading && !marketValueReport?.length ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : marketValueReportLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              animation="pulse"
              style={{ marginBottom: 8 }}
            />
          ) : (
            <>
              <Grid item xs={12} md={12} lg={12}>
                <ColumnGroupingTable
                  columns={columns}
                  datas={datasForTotal(true)}
                  loading={marketValueReportLoading}
                  tableHeight={300}
                  freezeParentColumnCount={1}
                  disablePagination={true}
                  applyThousandsSeparator={true}
                />
              </Grid>
              <br />
              <Grid item xs={12} md={12} lg={12}>
                <ColumnGroupingTable
                  columns={columns}
                  datas={preprocessedData(true)}
                  loading={marketValueReportLoading}
                  tableHeight={300}
                  freezeParentColumnCount={1}
                  disablePagination={true}
                  applyThousandsSeparator={true}
                />
              </Grid>
            </>
          )}
        </Grid>
        {!marketValueReportLoading &&
          marketValueReport?.length > 0 &&
          !marketValueReportError && (
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <Box sx={{ marginBottom: 2 }}>
                {false &&
                  toSave &&
                  !toCompare &&
                  (isWritableSfc || isAdmin) && (
                    <div className={classes.SaveBtn}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveDV100PercentInEquity}
                        sx={{
                          fontSize: "0.8rem",
                          boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                          height: "40px",
                        }}
                        disabled={metricsHistoryUpsertAndSaveLoading}
                      >
                        Save Data
                      </Button>
                    </div>
                  )}
              </Box>
            </Grid>
          )}
      </Grid>
    </Card>
  );
};

export default EVESensitivityModelerGlobalFiltersAndReports;
